import React, { useEffect, useMemo, useRef } from "react"
import {
  Box, Center, Text, useToken,
} from "@chakra-ui/react"
import {
  BarController, BarElement, CategoryScale, Chart as ChartJS, Filler, LinearScale,
  LineController, LineElement, PointElement, Tooltip,
} from "chart.js"
import { Chart } from "react-chartjs-2"
import { EventActivity } from "sharedTypes"
import Spinner from "components/elements/Spinner"
import { useEventActivity } from "queries/events"
import { asMoney } from "utilities/strings"
import {
  API_INTERVAL, ActivityInterval, ActivityRange, PeriodTotals, defaultRange, fillPeriods,
  periodLabel, periodTotals, periodsBetween,
} from "../activitySeries"

ChartJS.register(
  BarController, BarElement, CategoryScale, Filler, LinearScale,
  LineController, LineElement, PointElement, Tooltip,
)

export type ActivityMetric = "registrations" | "ticketing" | "checkins"

const valuesByPeriod = (data: EventActivity, metric: ActivityMetric) => {
  if (metric === "ticketing") {
    return Object.fromEntries(data.sales.map(({ period, net }) => [period, net]))
  }
  if (metric === "checkins") {
    return Object.fromEntries(data.checkIns.map(({ period, count }) => [period, count]))
  }

  return Object.fromEntries(data.registrations.map(({ period, ...types }) => [
    period, Object.values(types).reduce((sum, count) => sum + count, 0),
  ]))
}

export type PickedPeriod = { label: string, totals: PeriodTotals }

type Props = {
  eventId: string
  timezone: string
  currency: string
  metric: ActivityMetric
  interval: ActivityInterval
  color: string
  range?: ActivityRange
  onPick?: (picked: PickedPeriod | null) => void
}

const ActivityChart = ({
  eventId, timezone, currency, metric, interval, color, range: pickedRange, onPick,
}: Props) => {
  const range = pickedRange ?? defaultRange(interval, new Date(), timezone)
  const { data, isLoading, isError } = useEventActivity(
    eventId,
    { interval: API_INTERVAL[interval], ...range },
  )
  const [lineColor, gridColor, textColor, pointFill] = useToken(
    "colors",
    [color, "gray.200", "gray.600", "white"],
  )
  const box = useRef<HTMLDivElement>(null)
  const pickedPeriod = useRef<string | null>(null)
  const lastPointerEvent = useRef<Event | null>(null)

  const pick = (period: string | null) => {
    pickedPeriod.current = period
    onPick?.(period && data ? {
      label: periodLabel(interval, period),
      totals: periodTotals(data, period),
    } : null)
  }

  useEffect(() => {
    pickedPeriod.current = null
  }, [metric, interval])

  useEffect(() => {
    if (pickedPeriod.current && data) pick(pickedPeriod.current)
  }, [data])

  const latestPick = useRef(pick)
  latestPick.current = pick

  // Touch screens never send mouseleave.
  useEffect(() => {
    const clear = ({ target }: PointerEvent) => {
      if (pickedPeriod.current && !box.current?.contains(target as Node)) latestPick.current(null)
    }
    document.addEventListener("pointerdown", clear)

    return () => document.removeEventListener("pointerdown", clear)
  }, [])

  // Kept stable between renders, and replays skipped: Chart.js replays its last pointer event
  // on every redraw, which would pick the period again.
  const chart = useMemo(() => {
    if (!data) return null

    const values = valuesByPeriod(data, metric)
    // All time starts at the first period with data.
    const from = range.from ?? Object.keys(values).sort()[0]?.slice(0, 10)
    const periods = from ? periodsBetween(interval, from, range.to) : []
    const series = fillPeriods(periods, values)
    const asBars = interval === "hourly" || interval === "weekly" || interval === "monthly"
    const format = (value: number) => (
      metric === "ticketing" ? asMoney(value, currency, true) : value.toLocaleString()
    )

    return {
      periods,
      type: asBars ? "bar" as const : "line" as const,
      data: {
        labels: periods.map((period) => periodLabel(interval, period)),
        datasets: [{
          data: series,
          borderColor: lineColor,
          backgroundColor: asBars ? lineColor : `${lineColor}22`,
          fill: !asBars,
          cubicInterpolationMode: "monotone" as const,
          pointRadius: asBars || periods.length > 60 ? 0 : 2.5,
          pointBackgroundColor: pointFill,
          pointBorderWidth: 1.5,
          pointHoverRadius: 4,
          borderRadius: asBars ? 4 : 0,
        }],
      },
      options: {
        maintainAspectRatio: false,
        interaction: { mode: "index" as const, intersect: false },
        onHover: (event, elements) => {
          const native = event?.native ?? null
          if (native && native === lastPointerEvent.current) return
          lastPointerEvent.current = native

          const period = periods[elements[0]?.index] ?? null
          if (period !== pickedPeriod.current) latestPick.current(period)
        },
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: (item) => format(Number(item.raw)) } },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: textColor, maxTicksLimit: 8, maxRotation: 0 },
          },
          y: {
            beginAtZero: true,
            suggestedMax: 10,
            grid: { color: gridColor },
            ticks: {
              color: textColor,
              precision: 0,
              maxTicksLimit: 3,
              callback: (value) => format(Number(value)),
            },
          },
        },
      },
    }
  }, [
    data, metric, interval, range.from, range.to, currency,
    lineColor, gridColor, textColor, pointFill,
  ])

  if (isError) {
    return (
      <Center h="240px"><Text color="gray.600">The chart couldn&apos;t be loaded.</Text></Center>
    )
  }
  if (isLoading || !chart) {
    return <Center h="240px"><Spinner /></Center>
  }
  if (!chart.periods.length) {
    return <Center h="240px"><Text color="gray.600">Nothing in this period yet.</Text></Center>
  }

  return (
    <Box ref={box} h="240px" data-testid="activity-chart" onMouseLeave={() => pick(null)}>
      <Chart type={chart.type} data={chart.data} options={chart.options} />
    </Box>
  )
}

export default ActivityChart
